/**
 * Supabase auth-js persists the session in localStorage at
 * `sb-<project-ref>-auth-token` (plus `-code-verifier` and `-user`).
 * The default signOut() scope is global and revokes every device.
 * scope: 'local' revokes only this phone so a spouse's phone stays signed in.
 * signOut deletes the key only after the logout request succeeds
 * (401, 403, and 404 are treated as already signed out). A network failure
 * returns before the key is removed, so logout has to delete it itself.
 * On iOS the session also lives in Capacitor Preferences. That copy is cleared here too.
 */

export function isSupabaseAuthStorageKey(name) {
  return typeof name === 'string' && name.startsWith('sb-') && name.includes('-auth-token');
}

export function clearSupabaseAuthToken(storage) {
  if (
    !storage
    || typeof storage.length !== 'number'
    || typeof storage.key !== 'function'
    || typeof storage.removeItem !== 'function'
  ) {
    return [];
  }
  const names = [];
  for (let i = 0; i < storage.length; i += 1) {
    const name = storage.key(i);
    if (isSupabaseAuthStorageKey(name)) names.push(name);
  }
  names.forEach((name) => storage.removeItem(name));
  return names;
}

export async function signOutAndClearAuthToken(client, storage) {
  const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
  try {
    if (client && client.auth && typeof client.auth.stopAutoRefresh === 'function') {
      await client.auth.stopAutoRefresh();
    }
  } catch {
    // A failed refresh stop must not keep the token on disk.
  }
  try {
    if (client && client.auth && typeof client.auth.signOut === 'function') {
      const result = await client.auth.signOut({ scope: 'local' });
      if (result && result.error) {
        return result.error;
      }
    }
    return null;
  } catch (error) {
    return error;
  } finally {
    clearSupabaseAuthToken(store);
    try {
      const { clearNativeSupabaseAuth } = await import('./supabaseAuthStorage');
      await clearNativeSupabaseAuth();
    } catch (error) {
      // The WebView copy is already gone. A native clear failure must not throw.
    }
  }
}
